import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Card,
  CardContent,
  Grid,
  TextField,
} from '@mui/material';
import type { MaterialConsumption as MaterialConsumptionType } from '../types/budget';

interface MaterialConsumptionProps {
  orderQty: number;
  yarnItems: any[];
}

export const MaterialConsumption: React.FC<MaterialConsumptionProps> = ({
  orderQty,
  yarnItems,
}) => {
  // Calculate material consumption based on Excel formulas
  const calculateMaterialConsumption = (): MaterialConsumptionType => {
    const totalYarnQty = yarnItems.reduce((sum, item) => sum + item.qty, 0);
    
    // Basic consumption calculations (can be refined based on actual Excel formulas)
    const yarnPerDz = orderQty > 0 ? (totalYarnQty / orderQty) * 12 : 0;
    const lycraPerDz = yarnPerDz * 0.05; // 5% lycra blend
    const greyConsPerDz = yarnPerDz * 1.1; // 10% processing loss
    const fabricConsPerDz = yarnPerDz * 1.15; // 15% processing loss
    const darkColor = 30; // Percentage - can be made dynamic
    const lightColor = 40; // Percentage - can be made dynamic
    const white = 30; // Percentage - can be made dynamic
    const wash = 0; // Percentage - can be made dynamic
    const total = yarnPerDz + lycraPerDz;

    return {
      yarnPerDz,
      lycraPerDz,
      greyConsPerDz,
      fabricConsPerDz,
      darkColor,
      lightColor,
      white,
      wash,
      total,
    };
  };

  const consumption = calculateMaterialConsumption();

  return (
    <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
      <Typography variant="h5" gutterBottom>
        Material Consumption
      </Typography>
      <Divider sx={{ mb: 3 }} />
      
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3 }}>
        {/* Yarn Consumption */}
        <Box sx={{ gridColumn: { xs: '1', md: 'span 6' } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Yarn Consumption
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Per Dozen:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.yarnPerDz.toFixed(2)} kg
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Total:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(consumption.yarnPerDz * (orderQty / 12)).toFixed(2)} kg
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Lycra Consumption */}
        <Box sx={{ gridColumn: { xs: '1', md: 'span 6' } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Lycra Consumption
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Per Dozen:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.lycraPerDz.toFixed(2)} kg
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(consumption.lycraPerDz * (orderQty / 12)).toFixed(2)} kg
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Grey Fabric Consumption */}
        <Box sx={{ gridColumn: { xs: '1', md: 'span 6' } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Grey Fabric Consumption
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Per Dozen:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.greyConsPerDz.toFixed(2)} kg
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(consumption.greyConsPerDz * (orderQty / 12)).toFixed(2)} kg
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Finished Fabric Consumption */}
        <Box sx={{ gridColumn: { xs: '1', md: 'span 6' } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Finished Fabric Consumption
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Per Dozen:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.fabricConsPerDz.toFixed(2)} kg
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(consumption.fabricConsPerDz * (orderQty / 12)).toFixed(2)} kg
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Color Breakdown */}
        <Box sx={{ gridColumn: { xs: '1', md: 'span 6' } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Color Breakdown
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Dark Color:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.darkColor}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Light Color:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.lightColor}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">White:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.white}%
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Wash:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.wash}%
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>

        {/* Summary */}
        <Box sx={{ gridColumn: { xs: '1', md: 'span 6' } }}>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" color="primary" gutterBottom>
                Consumption Summary
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Total Yarn:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {consumption.total.toFixed(2)} kg/dz
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2">Order Quantity:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {orderQty} pcs
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="body2">Total Dozens:</Typography>
                <Typography variant="body2" fontWeight="bold">
                  {(orderQty / 12).toFixed(2)} dz
                </Typography>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Box>
    </Paper>
  );
};
